import { useCallback, useEffect, useMemo, useState } from "react";
import { todayIST } from "@/lib/istDate";
import { Calendar, RefreshCcw, FileDown, Filter, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, getToken, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useReportsBackLink } from "@/components/reports/useReportsBackLink";
import PageHeader from "@/components/PageHeader";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const STATUS_COLORS = {
  present: "bg-green-100 text-green-800",
  half_day: "bg-orange-100 text-orange-700",
  in_progress: "bg-sky-100 text-sky-700",
  absent: "bg-red-100 text-red-700",
  leave: "bg-yellow-100 text-yellow-800",
  holiday: "bg-yellow-100 text-yellow-800",
  weekly_off: "bg-purple-100 text-purple-700",
  tour: "bg-blue-100 text-blue-700",
  partial: "bg-orange-100 text-orange-700",
  flagged_radius: "bg-red-100 text-red-700",
  future: "bg-neutral-100 text-neutral-400",
};

export default function AttendanceReportPage() {
  const reportsBackTo = useReportsBackLink();
  const today = todayIST();
  const from30 = todayIST();   // opens on today
  const [filters, setFilters] = useState({
    date_from: from30, date_to: today, zone_id: "", sub_zone_id: "", user_id: "", status: "",
  });
  const [report, setReport] = useState(null);
  const [zones, setZones] = useState([]);
  const [subZones, setSubZones] = useState([]);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  useEffect(() => {
    Promise.all([
      api.get("/zones"),
      api.get("/users", { params: { page_size: 200 } }),
    ]).then(([{ data: z }, { data: u }]) => {
      setZones(z.items || []);
      setUsers((u.items || []).filter((x) => ["SE", "ASM", "RSM", "ZSM", "SALES_HEAD"].includes(x.role_code)));
    });
  }, []);

  // Phase 24A — cascading sub-zone dropdown
  useEffect(() => {
    if (!filters.zone_id) { setSubZones([]); return; }
    api.get("/geography/sub-zones", { params: { zone_id: filters.zone_id } })
       .then(r => setSubZones(r.data.items || []))
       .catch(() => setSubZones([]));
  }, [filters.zone_id]);

  const params = useMemo(() => {
    const p = {
      date_from: filters.date_from, date_to: filters.date_to,
      page, page_size: pageSize,
    };
    if (filters.zone_id) p.zone_id = filters.zone_id;
    if (filters.sub_zone_id) p.sub_zone_id = filters.sub_zone_id;
    if (filters.user_id) p.user_id = filters.user_id;
    if (filters.status) p.status = filters.status;
    return p;
  }, [filters, page]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/reports/attendance", { params });
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const doExport = async (format) => {
    setExporting(true);
    try {
      const qs = new URLSearchParams({ ...params, format });
      // remove pagination params from export url
      qs.delete("page"); qs.delete("page_size");
      const url = `${API_BASE}/reports/attendance/export?${qs.toString()}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error?.message || j.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = `attendance_${filters.date_from}_to_${filters.date_to}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(u);
      toast.success(`Downloaded ${format.toUpperCase()}`);
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setExporting(false); }
  };

  const summary = report?.summary || {};

  return (
    <div className="space-y-4" data-testid="attendance-report-page">
      <PageHeader
        breadcrumbs={[{ label: "Reports", to: reportsBackTo }, { label: "Attendance" }]}
        title="Attendance report"
        subtitle="Punch-in / punch-out matrix with GPS + selfie compliance flags."
        favorite={{ key: "reports.attendance", type: "screen", label: "Attendance report", route: "/reports/attendance" }}
        testid="attendancereportpage-page-header"
      />

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Attendance report</h1>
          <p className="text-sm text-neutral-500">Classified Present/Half day/Absent/Leave/Holiday/Weekly-off/Tour with branded exports. Roles with an attendance policy are judged on first-call to last-call market hours.</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="tulsi-gradient text-white" disabled={exporting} data-testid="report-export-menu">
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
              Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => doExport("xlsx")} data-testid="report-export-xlsx">
              Branded Excel (.xlsx)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => doExport("pdf")} data-testid="report-export-pdf">
              Branded PDF (.pdf)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* flex-wrap, not grid-cols-N + sm:/md:/lg: — those breakpoints key
          off the browser viewport, not this component's actual rendered
          width. Reused inside the narrow (~480px) field/manager shell,
          which is often opened in a full-width desktop browser, so a
          viewport breakpoint fires even though the visible column is
          phone-narrow, cramming every filter into one unreadable row. */}
      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-4 flex flex-wrap gap-3">
          <div className="w-[47%] min-w-[140px] flex-1">
            <FieldDate label="From" value={filters.date_from} onChange={(v) => setFilters((s) => ({ ...s, date_from: v }))} testid="report-date-from" />
          </div>
          <div className="w-[47%] min-w-[140px] flex-1">
            <FieldDate label="To" value={filters.date_to} onChange={(v) => setFilters((s) => ({ ...s, date_to: v }))} testid="report-date-to" />
          </div>
          <div className="w-[47%] min-w-[140px] flex-1">
            <Label className="text-[11px]">Zone</Label>
            <Select value={filters.zone_id || "all"}
                    onValueChange={(v) => setFilters((s) => ({ ...s, zone_id: v === "all" ? "" : v, sub_zone_id: "" }))}>
              <SelectTrigger data-testid="report-zone"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All zones</SelectItem>
                {zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[47%] min-w-[140px] flex-1">
            <Label className="text-[11px]">Sub-Zone</Label>
            <Select value={filters.sub_zone_id || "all"}
                    onValueChange={(v) => setFilters((s) => ({ ...s, sub_zone_id: v === "all" ? "" : v }))}
                    disabled={!filters.zone_id}>
              <SelectTrigger data-testid="report-subzone"><SelectValue placeholder={filters.zone_id ? "All sub-zones" : "Pick zone"} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sub-zones</SelectItem>
                {subZones.map((sz) => <SelectItem key={sz.id} value={sz.id}>{sz.code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[47%] min-w-[140px] flex-1">
            <Label className="text-[11px]">User</Label>
            <Select value={filters.user_id || "all"}
                    onValueChange={(v) => setFilters((s) => ({ ...s, user_id: v === "all" ? "" : v }))}>
              <SelectTrigger data-testid="report-user"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All users</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role_code})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[47%] min-w-[140px] flex-1">
            <Label className="text-[11px]">Status</Label>
            <Select value={filters.status || "all"}
                    onValueChange={(v) => setFilters((s) => ({ ...s, status: v === "all" ? "" : v }))}>
              <SelectTrigger data-testid="report-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {Object.keys(STATUS_COLORS).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Summary strip — flex-wrap (width-based), not grid-cols + viewport
          breakpoints; see note above. */}
      <div className="flex flex-wrap gap-2" data-testid="report-summary-strip">
        {Object.entries(summary).map(([k, v]) => (
          <div key={k} className={`rounded-xl p-3 min-w-[90px] flex-1 ${STATUS_COLORS[k] || "bg-neutral-100"}`}>
            <div className="text-[10px] uppercase tracking-widest font-semibold">{k === "late_days" ? "late days (after on-time)" : k.replace(/_/g, " ")}</div>
            <div className="text-xl font-extrabold">{v}</div>
          </div>
        ))}
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="report-table">
            <thead className="bg-neutral-50 text-[11px] uppercase text-neutral-500 tracking-widest">
              <tr>
                <th className="text-left py-3 px-3">User</th>
                <th className="text-left py-3 px-3">Role</th>
                <th className="text-left py-3 px-3">Zone</th>
                <th className="text-left py-3 px-3">Date</th>
                <th className="text-left py-3 px-3">Status</th>
                <th className="text-left py-3 px-3">Detail</th>
                <th className="text-left py-3 px-3">Punch In</th>
                <th className="text-left py-3 px-3">Punch Out</th>
                <th className="text-left py-3 px-3">First Call</th>
                <th className="text-left py-3 px-3">Last Call End</th>
                <th className="text-right py-3 px-3">Hours</th>
                <th className="text-left py-3 px-3">Late</th>
              </tr>
            </thead>
            <tbody>
              {busy && <tr><td colSpan={12} className="py-10 text-center text-neutral-400">Loading…</td></tr>}
              {!busy && (report?.items || []).length === 0 && (
                <tr><td colSpan={12} className="py-10 text-center text-neutral-400">No records in range</td></tr>
              )}
              {(report?.items || []).map((r, i) => (
                <tr key={`${r.user_id}-${r.date}-${i}`} className="border-t border-neutral-100">
                  <td className="py-2.5 px-3 font-medium">{r.name}</td>
                  <td className="py-2.5 px-3 text-neutral-500">{r.role_code}</td>
                  <td className="py-2.5 px-3 text-neutral-500">{r.zone}</td>
                  <td className="py-2.5 px-3 font-mono text-[12px]">{r.date}</td>
                  <td className="py-2.5 px-3">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${STATUS_COLORS[r.status] || ""}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="py-2.5 px-3 text-neutral-500 text-[12px]">{r.sub_status || "—"}</td>
                  <td className="py-2.5 px-3 font-mono text-[12px]">{fmtTime(r.punch_in_ts)}</td>
                  <td className="py-2.5 px-3 font-mono text-[12px]">{fmtTime(r.punch_out_ts)}</td>
                  <td className="py-2.5 px-3 font-mono text-[12px]">{fmtTime(r.first_call_ts)}</td>
                  <td className="py-2.5 px-3 font-mono text-[12px]">{fmtTime(r.last_call_ts)}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-[12px]">{fmtDur(r.working_seconds)}</td>
                  <td className="py-2.5 px-3 text-[12px] text-red-600 font-semibold">{r.late || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {report && (
        <div className="flex items-center justify-between text-xs text-neutral-500">
          <div>Page {report.page} · {report.items.length} of {report.total}</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                    data-testid="report-prev">Prev</Button>
            <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)}
                    disabled={page * pageSize >= report.total} data-testid="report-next">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldDate({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-[11px]">{label}</Label>
      <Input type="date" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return "—"; }
}
function fmtDur(sec) {
  if (!Number.isFinite(sec)) return "—";
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
}
